/**
 * TemplateCard - Carte individuelle pour un template de prompt
 * 
 * Affiche les informations d'un template et permet de l'utiliser
 */
import React, { useState } from 'react'
import { Check, Eye, Sparkles, Zap, Crown } from 'lucide-react'
import { PromptTemplate } from '../data/promptTemplates'
import { useAppLanguage } from '../i18n'

interface TemplateCardProps {
  template: PromptTemplate
  onUse: (template: PromptTemplate) => void
  isUsed?: boolean
}

export default function TemplateCard({ template, onUse, isUsed = false }: TemplateCardProps) {
  const [showPreview, setShowPreview] = useState(false)
  const { t } = useAppLanguage()
  const ae = t.agents.agentEditor

  const getLevelIcon = () => {
    switch (template.level) {
      case 'basic':
        return <Zap className="w-4 h-4" />
      case 'advanced':
        return <Sparkles className="w-4 h-4" />
      case 'premium':
        return <Crown className="w-4 h-4" />
    }
  }

  const getLevelColor = () => {
    switch (template.level) {
      case 'basic':
        return 'bg-blue-900/20 text-blue-400 border-blue-800/30'
      case 'advanced':
        return 'bg-purple-900/20 text-purple-400 border-purple-800/30'
      case 'premium':
        return 'bg-amber-900/20 text-amber-400 border-amber-800/30'
    }
  }

  const getLevelLabel = () => {
    switch (template.level) {
      case 'basic':
        return ae.levelBasic
      case 'advanced':
        return ae.levelAdvanced
      case 'premium':
        return ae.levelPremium
    }
  }

  return (
    <div
      className={`relative rounded-lg border transition-all ${
        isUsed
          ? 'bg-emerald-900/20 border-emerald-600'
          : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
      }`}
    >
      {/* Badge recommandé */}
      {template.recommended && !isUsed && (
        <div className="absolute -top-2 -right-2 z-10">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500 text-white text-xs font-medium shadow-lg">
            ⭐ {ae.badgeRecommended}
          </span>
        </div>
      )}

      {/* Badge utilisé */}
      {isUsed && (
        <div className="absolute -top-2 -right-2 z-10">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500 text-white text-xs font-medium shadow-lg">
            <Check className="w-3 h-3" />
            {ae.badgeUsed}
          </span>
        </div>
      )}

      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-zinc-100 mb-1">
              {template.name}
            </h4>
            <p className="text-xs text-zinc-400">
              {template.description}
            </p>
          </div>
          
          <div className={`ml-2 flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-medium ${getLevelColor()}`}>
            {getLevelIcon()}
            {getLevelLabel()}
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-3">
          {template.tags.slice(0, 3).map((tag, idx) => (
            <span
              key={idx}
              className="inline-flex items-center px-2 py-0.5 rounded-md bg-zinc-700/50 text-zinc-400 text-xs"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Popularité */}
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-zinc-500">{ae.labelPopularity}</span>
            <span className="text-zinc-400 font-medium">{template.popularity}%</span>
          </div>
          <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all"
              style={{ width: `${template.popularity}%` }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-2 rounded-lg border border-zinc-600 text-zinc-300 hover:bg-zinc-700/50 transition-colors text-xs whitespace-nowrap"
          >
            <Eye className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{showPreview ? ae.btnHide : ae.btnPreview}</span>
          </button>
          
          <button
            type="button"
            onClick={() => onUse(template)}
            disabled={isUsed}
            className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-2 rounded-lg font-medium transition-colors text-xs whitespace-nowrap ${
              isUsed
                ? 'bg-emerald-600/50 text-emerald-200 cursor-not-allowed'
                : 'bg-emerald-600 text-white hover:bg-emerald-500'
            }`}
          >
            {isUsed ? (
              <>
                <Check className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{ae.badgeUsed}</span>
              </>
            ) : (
              <>
                <span>{ae.btnUse}</span>
              </>
            )}
          </button>
        </div>

        {/* Preview */}
        {showPreview && (
          <div className="mt-3 pt-3 border-t border-zinc-700">
            <div className="max-h-48 overflow-y-auto">
              <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-mono">
                {template.content}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
